import { forwardRef, useState } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Password input with eye toggle to show/hide the value.
 * Drop-in replacement for `<Input type="password" .../>`.
 *
 * Accepts all standard <Input> props (value, onChange, placeholder, className,
 * required, autoComplete, data-testid, etc.). Forwards refs to the underlying input.
 */
export const PasswordInput = forwardRef(({
  className = '',
  inputClassName = '',
  toggleTestId,
  ...props
}, ref) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={`pr-10 ${inputClassName}`}
        {...props}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
        data-testid={toggleTestId || (props.id ? `${props.id}-toggle` : 'password-toggle')}
        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-slate-500 hover:text-slate-900 hover:bg-transparent"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </Button>
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';
